/**
 * « Paramètres de connexion » (admin) : comptes de diffusion reliés
 * (`social_oauth_accounts`), état des clés du serveur et tests réels (edge fn
 * `admin-connections`), connexion OAuth qui revient sur la page.
 *
 * Réponses vérifiées par Zod (edge fn hors des types générés).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';
import { ytDb } from './youtube-queries';
import { startSocialOAuth, type SocialProvider } from './social-client';

export type AccountPlatform = 'youtube' | 'tiktok' | 'instagram' | 'facebook' | 'linkedin';

export const DIFFUSION_ACCOUNTS: readonly {
  id: AccountPlatform;
  label: string;
  provider: SocialProvider;
  uses: string;
}[] = [
  {
    id: 'youtube',
    label: 'YouTube',
    provider: 'google',
    uses: 'Shorts : animations, exercices, défis.',
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    provider: 'tiktok',
    uses: 'Vidéos des animations et des défis.',
  },
  {
    id: 'instagram',
    label: 'Instagram',
    provider: 'meta',
    uses: 'Reels, carrousels du blog. Relié avec Facebook (Meta).',
  },
  {
    id: 'facebook',
    label: 'Facebook',
    provider: 'meta',
    uses: 'Articles du blog sur la Page. Relié avec Instagram (Meta).',
  },
  { id: 'linkedin', label: 'LinkedIn', provider: 'linkedin', uses: 'Articles du blog.' },
];

export const accountRowSchema = z.object({
  platform: z.string(),
  channel_title: z.string().nullable(),
  scopes: z.array(z.string()).nullable().catch(null),
  updated_at: z.string(),
});
export type DiffusionAccount = z.infer<typeof accountRowSchema>;

export const keyStatusSchema = z.object({
  name: z.string(),
  label: z.string(),
  group: z.string(),
  kind: z.enum(['key', 'secret', 'setting']),
  purpose: z.string(),
  set: z.boolean(),
  hint: z.string().nullable(),
  value: z.string().nullable(),
  test: z.string().nullable(),
});
export type ServerKeyStatus = z.infer<typeof keyStatusSchema>;

const appEnvSchema = z.object({ clientId: z.boolean(), secret: z.boolean() });

export const connectionsStatusSchema = z.object({
  keys: z.array(keyStatusSchema),
  apps: z.record(appEnvSchema),
});
export type ConnectionsStatus = z.infer<typeof connectionsStatusSchema>;

export const testResultSchema = z.object({
  target: z.string(),
  ok: z.boolean(),
  message: z.string(),
  detail: z.string().nullable().optional(),
});
export type ConnectionTestResult = z.infer<typeof testResultSchema>;

export const KEY_GROUP_LABEL: Record<string, string> = {
  ia: 'Intelligence artificielle',
  paiements: 'Paiements',
  emails: 'Emails',
  notifications: 'Notifications push',
  adresses: 'Adresses des sites',
  interne: 'Tâches internes et Live School',
};

export const connectionsKeys = {
  accounts: ['admin', 'connections', 'accounts'] as const,
  status: ['admin', 'connections', 'status'] as const,
};

export class ConnectionsError extends Error {}

async function invokeConnections(body: Record<string, unknown>): Promise<unknown> {
  const { data, error } = await supabase.functions.invoke('admin-connections', { body });
  if (error) {
    const ctx = (error as { context?: Response }).context;
    if (ctx?.status === 404)
      throw new ConnectionsError(
        'La fonction « admin-connections » n’est pas encore déployée (supabase functions deploy admin-connections).',
      );
    throw new ConnectionsError(`Le serveur n’a pas répondu (${error.message}).`);
  }
  return data;
}

/** Comptes reliés (un par plateforme). */
/**
 * La chaîne YouTube a-t-elle été reliée avant le droit de gérer les playlists
 * (0675) ? Les Shorts d'animations ne sont alors pas rangés dans leur playlist :
 * il faut la relier à nouveau. PUR.
 */
export function youtubeNeedsPlaylistScope(
  row: { platform: string; scopes?: readonly string[] | null } | null | undefined,
): boolean {
  return !!row && row.platform === 'youtube' && !(row.scopes ?? []).includes('youtube.force-ssl');
}

export function useDiffusionAccounts() {
  return useQuery({
    queryKey: connectionsKeys.accounts,
    queryFn: async (): Promise<DiffusionAccount[]> => {
      const { data, error } = await ytDb
        .from('social_oauth_accounts')
        .select('platform, channel_title, scopes, updated_at');
      if (error) throw new ConnectionsError(error.message);
      return z.array(accountRowSchema).parse(data ?? []);
    },
  });
}

/** Clés et réglages du serveur (jamais un secret). */
export function useConnectionsStatus() {
  return useQuery({
    queryKey: connectionsKeys.status,
    retry: false,
    queryFn: async (): Promise<ConnectionsStatus> => {
      const parsed = connectionsStatusSchema.safeParse(
        await invokeConnections({ action: 'status' }),
      );
      if (!parsed.success) throw new ConnectionsError('Réponse du serveur inattendue.');
      return parsed.data;
    },
  });
}

/** Essai réel d'un compte ou d'une clé (rien n'est publié). */
export async function testConnection(target: string): Promise<ConnectionTestResult> {
  const parsed = testResultSchema.safeParse(await invokeConnections({ action: 'test', target }));
  if (!parsed.success) throw new ConnectionsError('Réponse du serveur inattendue.');
  return parsed.data;
}

export function useTestConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: testConnection,
    // un jeton TikTok renouvelé, un compte relié : la liste peut avoir changé
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: connectionsKeys.accounts });
    },
  });
}

// ------------------------------------------------- connexion OAuth (aller-retour)

const RETURN_KEY = 'jr-oauth-return';
const RESULT_KEY = 'jr-oauth-result';
/** Au-delà, un retour OAuth n'est plus attendu (onglet laissé ouvert). */
export const OAUTH_RETURN_TTL_MS = 30 * 60 * 1000;

export interface OAuthReturn {
  platform: AccountPlatform;
  /** retour de `social-oauth-callback` (`?status=…&message=…`) ; absent pour Google */
  ok?: boolean;
  message?: string;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Avant de partir chez Google / Meta / TikTok / LinkedIn : revenir ici ensuite. */
export function rememberOAuthReturn(platform: AccountPlatform, now = Date.now()): void {
  storage()?.setItem(RETURN_KEY, JSON.stringify({ platform, at: now }));
}

/**
 * Sur une page de retour OAuth : la connexion venait-elle de « Paramètres de
 * connexion » ? Si oui, garde le résultat pour la page et dit d'y retourner.
 */
export function takeOAuthReturn(
  result: { ok?: boolean; message?: string } = {},
  now = Date.now(),
): boolean {
  const s = storage();
  const raw = s?.getItem(RETURN_KEY);
  if (!s || !raw) return false;
  s.removeItem(RETURN_KEY);
  try {
    const r = JSON.parse(raw) as { platform?: AccountPlatform; at?: number };
    if (!r.platform || typeof r.at !== 'number' || now - r.at > OAUTH_RETURN_TTL_MS) return false;
    s.setItem(RESULT_KEY, JSON.stringify({ platform: r.platform, ...result }));
    return true;
  } catch {
    return false;
  }
}

/** Sur « Paramètres de connexion » : le retour d'une connexion (lu, puis `clearOAuthResult`). */
export function peekOAuthResult(): OAuthReturn | null {
  const raw = storage()?.getItem(RESULT_KEY);
  if (!raw) return null;
  try {
    const r = JSON.parse(raw) as OAuthReturn;
    return DIFFUSION_ACCOUNTS.some((a) => a.id === r.platform) ? r : null;
  } catch {
    return null;
  }
}

export function clearOAuthResult(): void {
  storage()?.removeItem(RESULT_KEY);
}

/** URL de consentement du fournisseur du compte (Google pour YouTube). */
export async function oauthUrl(platform: AccountPlatform): Promise<string> {
  const provider = DIFFUSION_ACCOUNTS.find((a) => a.id === platform)!.provider;
  if (provider === 'google') {
    const { data, error } = await supabase.functions.invoke('youtube-oauth-start', { body: {} });
    const url = (data as { url?: unknown } | null)?.url;
    if (error || typeof url !== 'string')
      throw new ConnectionsError('Google n’a pas donné d’adresse de connexion : réessaie.');
    return url;
  }
  return (await startSocialOAuth(provider)).url;
}

/** Lien vers les secrets des edge functions dans Supabase (projet de l'app). */
export function supabaseSecretsUrl(supabaseUrl: string | undefined): string | null {
  try {
    const host = new URL(supabaseUrl ?? '').hostname;
    if (!host.endsWith('.supabase.co')) return null;
    return `https://supabase.com/dashboard/project/${host.split('.')[0]}/functions/secrets`;
  } catch {
    return null;
  }
}
